/**
 * Atbash SDK Telemetry — OpenTelemetry metrics for usage tracking.
 *
 * Tracks: function call counts, latency, source (CLI/plugin/SDK),
 * and agent identity. ON by default.
 *
 * Opt-out: create ~/.config/atbash/telemetry.json with { "enabled": false }
 * The file must be readable by the SDK process. If missing, corrupted, or
 * unreadable → telemetry stays ON. Environment variables cannot disable
 * telemetry (prevents agent bypass via env-var injection).
 */

import { readFileSync } from "node:fs";
import { homedir } from "node:os";
import { join } from "node:path";

import type { Counter, Histogram } from "@opentelemetry/api";
import { OTLPMetricExporter } from "@opentelemetry/exporter-metrics-otlp-http";
import { resourceFromAttributes } from "@opentelemetry/resources";
import {
  MeterProvider,
  PeriodicExportingMetricReader,
} from "@opentelemetry/sdk-metrics";
import { native } from "../native.js";

// ── Types ───────────────────────────────────────────────────────

export type ClientSource =
  | "cli"
  | "sdk"
  | "plugin:openclaw"
  | "plugin:langchain"
  | "plugin:langgraph"
  | "plugin:hermes"
  | "plugin:eliza"
  | "plugin:crewai"
  | "plugin:mcp"
  | "plugin:autogen"
  | "plugin:jeenai"
  | (string & {});

export interface TelemetryConfig {
  /** Must be true to send any telemetry. Default: false */
  enabled: boolean;
  /** Where calls originate */
  source?: ClientSource;
  /** Flush interval in ms. Default: 60000 */
  exportIntervalMs?: number;
}

// ── State (module-level so recordCall/recordDuration can access) ─

let meterProvider: MeterProvider | null = null;
let callCounter: Counter | null = null;
let durationHistogram: Histogram | null = null;
let defaultSource: ClientSource = "sdk";

// ── Setup ───────────────────────────────────────────────────────

/**
 * Check if telemetry is disabled via the protected config file.
 * Only ~/.config/atbash/telemetry.json with { "enabled": false } disables it.
 * Missing, corrupted, or unreadable file → telemetry stays ON.
 */
function isTelemetryOptedOut(): boolean {
  try {
    const home = process.env.HOME || homedir() || "";
    const filePath = join(home, ".config", "atbash", "telemetry.json");
    const raw = readFileSync(filePath, "utf-8").trim();
    if (!raw) return false;
    const config = JSON.parse(raw) as { enabled?: boolean };
    return config.enabled === false;
  } catch {
    return false; // missing/corrupted/unreadable → telemetry ON
  }
}

/**
 * Auto-initialize telemetry on first recordCall if not already set up.
 * Reads opt-out from the protected config file, not from environment
 * variables (env vars are too easy for an attacker-controlled agent to
 * clear).
 */
function autoInit(): void {
  if (meterProvider) return;
  if (isTelemetryOptedOut()) return;
  setupTelemetry({ enabled: true });
}

export function setupTelemetry(config: TelemetryConfig): void {
  if (!config.enabled) return;
  if (meterProvider) return; // already initialized
  if (isTelemetryOptedOut()) return; // protected file opt-out

  defaultSource = config.source ?? "sdk";

  // Built-in Atbash ingest key comes from the Rust core (feature-gated by
  // --features prod at build time). Safe to embed — the key only writes
  // metrics, cannot read or delete them. Empty in dev flavor → telemetry
  // silently no-ops. Runtime env var can still override for debugging.
  const apiKey = process.env.HONEYCOMB_API_KEY ?? native.HONEYCOMB_KEY;
  if (!apiKey) return; // dev flavor with no override → skip setup entirely

  const exporter = new OTLPMetricExporter({
    url: "https://api.honeycomb.io/v1/metrics",
    headers: {
      "x-honeycomb-team": apiKey,
    },
  });

  const reader = new PeriodicExportingMetricReader({
    exporter,
    exportIntervalMillis: config.exportIntervalMs ?? 60_000,
  });

  meterProvider = new MeterProvider({
    resource: resourceFromAttributes({
      "service.name": "atbash-sdk",
    }),
    readers: [reader],
  });

  const meter = meterProvider.getMeter("atbash-sdk");

  callCounter = meter.createCounter("atbash.sdk.function.calls", {
    description: "Number of SDK function calls",
  });

  durationHistogram = meter.createHistogram("atbash.sdk.function.duration_ms", {
    description: "SDK function execution duration",
    unit: "ms",
  });
}

// ── Recording ───────────────────────────────────────────────────

/**
 * Record a function call. Call at the START of each tracked function.
 * Safe to call even if telemetry is disabled — does nothing.
 */
export function recordCall(
  functionName: string,
  source?: ClientSource,
  agentPubkey?: string,
): void {
  autoInit();
  if (!callCounter) return;

  callCounter.add(1, {
    "function.name": functionName,
    source: source ?? defaultSource,
    ...(agentPubkey && { "agent.pubkey": agentPubkey }),
  });
}

/**
 * Record function duration. Call at the END of each tracked function.
 * Safe to call even if telemetry is disabled — does nothing.
 */
export function recordDuration(
  functionName: string,
  durationMs: number,
  status: "success" | "error",
  source?: ClientSource,
): void {
  if (!durationHistogram) return;

  durationHistogram.record(durationMs, {
    "function.name": functionName,
    status: status,
    source: source ?? defaultSource,
  });
}

// ── Shutdown ────────────────────────────────────────────────────

/**
 * Force-flush pending metrics without shutting down.
 * Use in short-lived processes (CLI) to ensure data is sent.
 */
export async function flushTelemetry(): Promise<void> {
  if (!meterProvider) return;
  await meterProvider.forceFlush();
}

/**
 * Flush pending metrics and shut down. Call before process exits.
 */
export async function shutdownTelemetry(): Promise<void> {
  if (!meterProvider) return;
  await meterProvider.shutdown();
  meterProvider = null;
  callCounter = null;
  durationHistogram = null;
}
